import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Box,
  LinearProgress,
  IconButton,
  Divider
} from '@mui/material';
import { Launch } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import '../styles/ContractDetails.css';

const ClientResources = () => {
  const { contractId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resources, setResources] = useState([]);

  useEffect(() => {
    fetchResources();
  }, [contractId]);

  const fetchResources = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/resources/${contractId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch resources');
      }

      const resourcesData = await response.json();
      console.log('Resources data:', resourcesData);
      
      setResources(resourcesData);
    } catch (err) {
      console.error('Error fetching resources:', err);
      setError('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const renderResourceCard = (resource) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={resource.resource_id}>
      <Card 
        sx={{ 
          height: 240,
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
          },
          cursor: 'pointer'
        }}
        onClick={() => window.open(resource.resource_link, '_blank')}
      >
        {/* Purple Banner with Link Icon */}
        <Box 
          sx={{ 
            height: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#8A42EC', // Brand purple
            color: 'white',
            position: 'relative'
          }}
        >
          <FontAwesomeIcon 
            icon={faLink} 
            style={{ 
              fontSize: '40px',
              width: '40px',
              height: '40px'
            }}
          />
          <IconButton
            sx={{ 
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.3)'
              }
            }}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              window.open(resource.resource_link, '_blank');
            }}
          >
            <Launch fontSize="small" />
          </IconButton>
        </Box>

        {/* Content */}
        <CardContent sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography 
            variant="subtitle1" 
            fontWeight={600} 
            sx={{ 
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.3,
              height: '2.6em'
            }}
          >
            {resource.resource_name}
          </Typography>

          {resource.resource_description && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.4,
                flex: 1
              }}
            >
              {resource.resource_description}
            </Typography>
          )}

          <Box sx={{ mt: 'auto', pt: 1 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                color: '#8A42EC',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Resource
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <>
      {/* Loading State */}
      {loading && <LinearProgress style={{ marginBottom: '20px' }} />}

      {/* Error State */}
      {error && (
        <Card style={{ padding: '20px', textAlign: 'center', marginBottom: '20px' }}>
          <Typography color="error">{error}</Typography>
        </Card>
      )}

      {/* Resources Grid */}
      {!loading && !error && (
        <Card className="details-card">
          <CardHeader 
            title={`${resources.length} resource${resources.length !== 1 ? 's' : ''}`}
          />
          <Divider />
          <CardContent>
            {resources.length > 0 ? (
              <Grid container spacing={3}>
                {resources.map(renderResourceCard)}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, color: '#888' }}>
                <Typography variant="h6" style={{ color: '#666', marginBottom: '8px' }}>
                  No resources found
                </Typography>
                <Typography variant="body2" style={{ color: '#999' }}>
                  No resources have been set up for this contract yet.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default ClientResources; 